import React, { useState, useEffect, useContext } from 'react';
import { formatDuration, formatDurationClock } from '../../../utils/timeUtils';
import { AuthContext } from '../../../context/AuthContext';
import { useMaterialSuggestions } from '../hooks/useMaterialSuggestions';
import { getLabelDisplayName } from '../../../shared_components/utils/FirebaseLabelsetsUtils';
import { useTranslation } from '../../../i18n/i18n';
import { filterPropertiesForDevice } from '../../../utils/recipes/recipeNormalization';
import { getIngredientMaterialClasses } from '../../../utils/recipes/ingredientMaterials';
import { useRecipeDisplay } from '../../../context/RecipeDisplayContext';

import Select from '../../../components/ui/Select';
import { MapPin, PieChart, Timer, Truck, X } from 'lucide-react';
import { cn } from '../../../utils/cn';
import EllipseTooltip from '../../../utils/EllipseTooltip';

// Function to generate consistent colors from material IDs
const getMaterialColor = (materialId) => {
  if (!materialId) return '#cccccc'; // Default gray for no material

  // // Pre-defined color palette - you can customize these colors
  // const colorPalette = [
  //     '#FF5733', '#33FF57', '#3357FF', '#FF33A6',
  //     '#33FFF5', '#F5FF33', '#FF5733', '#C233FF',
  //     '#FF8C33', '#33FFCE', '#8C33FF', '#FFCE33'
  // ];

  // secondary palette
  const colorPalette = [
    // Primary colors
    '#4285F4', // Google Blue
    '#34A853', // Google Green *
    '#EA4335', // Google Red
    '#FBBC05', // Google Yellow

    // Extended palette
    '#A142F4', // Purple
    '#24C1E0', // Cyan
    '#5E97F6', // Light Blue
    '#A1887F', // lighter Brown *

    // Muted tones (Airtable-like)
    '#6DfC41', // Bright Lime
    '#26A69A', // Soft Blue-Green Pastel *
    '#78909C', // Blue Grey
    '#7986CB', // Indigo
    '#FFCA28', // Amber

    // Soft pastels
    '#FF8A65', // Coral
    '#FF7043', // Deep Orange
    '#26A69A', // Green
    '#9CCC65', // Lime
  ];

  // Generate a numeric hash from the material ID string
  const hashCode = materialId.split('').reduce((acc, char, index) => {
    // Use both the character code and its position to create a more unique hash
    const charCode = char.charCodeAt(0);
    const position = index + 1;
    return acc + charCode * position;
  }, 0);

  // Use the hash to select a color from the palette
  return colorPalette[Math.abs(hashCode) % colorPalette.length];
};

/**
 * ButtonGrid component for the FeedTimestamping page
 * Displays a grid of supplier/field buttons that can be clicked to activate
 * or edited in edit mode
 */
const ButtonGrid = ({
  buttonSelection,
  nRows,
  nColumns,
  editMode,
  isButtonActive,
  selectionEdited,
  toggleSelection,
  suppliers = {},
  stockFields = {},
  activeDocument = null,
  productionRuns,
  ingredients,
  isRecipeControlEnabled,
}) => {
  const { currentUser, defaultLabelsetGroups, globalLabelsets } = useContext(AuthContext);
  const { t } = useTranslation(currentUser?.info?.languageCode);
  const { suggestedMaterials, isLoading, fetchSuggestedMaterial, clearSuggestedMaterial } = useMaterialSuggestions();
  const { activeDeviceId } = useRecipeDisplay();

  const materialLabels = defaultLabelsetGroups?.materials?.labels || {};

  const [, forceUpdate] = React.useReducer((x) => x + 1, 0);
  useEffect(() => {
    const timer = setInterval(forceUpdate, 500);
    return () => clearInterval(timer);
  }, []);

  // Conveyor-scoped like RecipeDisplay: another conveyor's ingredient is not part of this recipe.
  const currentIngredients = React.useMemo(() => {
    const now = new Date();
    const currentRun = productionRuns.find((run) => run.start_date <= now && run.end_date >= now);

    if (!currentRun?.production_properties) return null;

    return filterPropertiesForDevice(currentRun.production_properties, activeDeviceId).map((prop) => {
      const ingredient = ingredients[prop.ingredient_id];
      return {
        // An ingredient covers one material class per variant.
        materials: getIngredientMaterialClasses(ingredient, globalLabelsets),
        fields: prop.fields,
      };
    });
  }, [productionRuns, ingredients, activeDeviceId, globalLabelsets]);

  // console.log('Current Ingredients in ButtonGrid:', currentIngredients);

  /**
   * Check if a material and field combination is in the current recipe.
   * A button matches an ingredient when the ingredient covers the button's material class
   * *and* the recipe feeds it from the button's field.
   * Expects new field shape:
   * { field_1: { display_name: "...", field_id: "2" } }
   * Recalculates when currentIngredients changes (production starts, finishes, or changes).
   * @param {string} material - The material ID to check
   * @param {string} field - The field ID to check
   * @returns {boolean} - True if the material/field combination is in the recipe
   */
  const isIngredientInRecipe = React.useCallback(
    (material, field) => {
      if (!currentIngredients || !material || !field) {
        return true; // Default to true if no recipe data or incomplete selection
      }

      const extractFieldIds = (rawFields) => {
        if (rawFields == null || typeof rawFields !== 'object' || Array.isArray(rawFields)) return [];

        return Object.values(rawFields)
          .map((value) => (value && typeof value === 'object' ? (value.field_id ?? null) : null))
          .filter(Boolean)
          .map(String);
      };

      return currentIngredients.some((ing) => {
        if (!ing.materials.includes(String(material))) return false;
        const fieldIds = extractFieldIds(ing.fields);
        return fieldIds.includes(String(field));
      });
    },
    [currentIngredients],
  );

  // Add state to track dismissed notifications
  const [dismissedNotifications, setDismissedNotifications] = useState({});

  // Clear suggestions and dismissed notifications whenever the user leaves edit mode
  // (both Save and Back/cancel), so stale state never bleeds into the next session.
  useEffect(() => {
    if (!editMode) {
      Object.keys(suggestedMaterials).forEach((index) => clearSuggestedMaterial(Number(index)));
      setDismissedNotifications({});
    }
  }, [editMode]);

  // Function to dismiss a notification
  const dismissNotification = (index) => {
    setDismissedNotifications((prev) => ({
      ...prev,
      [index]: true,
    }));
  };

  // Handle material selection and apply suggestion
  const handleMaterialChange = (index, value) => {
    // Clear any suggestion when the user manually selects a material
    clearSuggestedMaterial(index);
    selectionEdited(index, 'material', value);
  };

  // Handle supplier or field change to fetch material suggestions
  const handleFieldOrSupplierChange = (index, key, value) => {
    // Always clear material when changing field or supplier
    // Clear material first to avoid state conflicts
    selectionEdited(index, 'material', '');
    clearSuggestedMaterial(index);

    // Then update the field/supplier
    selectionEdited(index, key, value);

    // Check if we can fetch suggestions with the updated values
    const updatedField = key === 'field' ? value : buttonSelection[index].field;
    const updatedSupplier = key === 'supplier_id' ? value : buttonSelection[index].supplier_id;

    // Only fetch suggestion if both supplier and field will be set
    if (updatedSupplier && updatedField) {
      const supplierId = updatedSupplier;
      const fieldId = updatedField;
      const companyId = currentUser?.company?.company;

      // Values are computed locally so we can fetch immediately without waiting for state
      fetchSuggestedMaterial(supplierId, fieldId, companyId, index, globalLabelsets);
    }
  };

  // Apply suggestion to the material dropdown
  useEffect(() => {
    // For each button with a suggestion
    Object.entries(suggestedMaterials).forEach(([index, suggestion]) => {
      if (suggestion.found && suggestion.material) {
        const buttonIndex = parseInt(index);
        const currentButton = buttonSelection[buttonIndex];

        // Debug material matching
        // Only auto-select if the material exists in options, and the field and supplier are set
        if (currentButton.supplier_id && currentButton.field) {
          if (currentButton.material !== suggestion.material) {
            if (Object.hasOwn(materialLabels, suggestion.material)) {
              selectionEdited(buttonIndex, 'material', suggestion.material);
            } else {
              console.warn('Material found in delivery but does not exist in dropdown options:', suggestion.material);
            }
          }
        }
      }
    });
  }, [suggestedMaterials, buttonSelection, materialLabels, selectionEdited]);

  const supplierOptions = Object.entries(suppliers)
    .map(([key, value]) => {
      if (value?.supplier_code) {
        return { value: key, label: `${value?.supplier_code} ${value?.display_name ?? ''}` };
      }
      return { value: key, label: value?.display_name ?? '' };
    })
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));

  const fieldOptions = Object.entries(stockFields)
    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB, undefined, { numeric: true }))
    .map(([key, value]) => ({ value: key, label: value?.display_name ?? '' }));

  const materialOptions = Object.keys(materialLabels)
    .sort((keyA, keyB) => keyA.localeCompare(keyB, undefined, { numeric: true }))
    .map((key) => ({
      value: key,
      label: getLabelDisplayName(defaultLabelsetGroups?.materials, key, currentUser?.info?.languageCode) || key,
    }));

  return (
    <div className='flex min-h-0 flex-1 flex-col overflow-hidden'>
      <div className='min-h-0 flex-1 overflow-y-auto p-6'>
        <div
          className='grid min-h-full items-stretch gap-3.5'
          style={{
            gridTemplateRows: `repeat(${nRows}, minmax(auto, 1fr))`,
            gridTemplateColumns: `repeat(${nColumns}, minmax(0, 1fr))`,
          }}
        >
          {buttonSelection.map((selection, i) => {
            if (editMode) {
              const isCompact = nRows > 2 || nColumns > 4;
              const suggestion = suggestedMaterials[i];
              const isLoadingCurrentButton = isLoading[i];

              const showSuggestionMessage = suggestion?.found && !dismissedNotifications[i];
              const suggestionMessage = t('button_grid_material_auto_set');

              return (
                <div
                  key={i}
                  className='relative flex min-h-0 flex-col gap-2 rounded-2xl p-4 py-3 shadow-[0_1px_3px_0_rgba(0,0,0,0.10),0_1px_2px_-1px_rgba(0,0,0,0.10)] dark:border-white/[8%] bg-[#E7EEF7] dark:bg-[#22293B] justify-center'
                >
                  <div>
                    {/* Supplier section */}
                    {!isCompact ? (
                      // Non-compact mode: Icon next to label
                      <p className='flex items-center gap-2 !m-0 !ml-0.5 !mb-1'>
                        <Truck className='size-5' />
                        <span className='text-base'>{t('button_grid_supplier_label')}</span>
                      </p>
                    ) : null}

                    <Select
                      prefix={isCompact ? <Truck className='size-4' /> : null}
                      className='w-full border-none'
                      value={selection.supplier_id || ''}
                      options={supplierOptions}
                      placeholder={t('button_grid_supplier_placeholder')}
                      hasDeselectOption
                      onChange={(value) => handleFieldOrSupplierChange(i, 'supplier_id', value)}
                    />
                  </div>

                  <div>
                    {/* Field section */}
                    {!isCompact ? (
                      // Non-compact mode: Icon next to label
                      <p className='flex items-center gap-2 !m-0 !ml-0.5 !mb-1'>
                        <MapPin className='size-5' />
                        <span className='text-base'>{t('button_grid_field_label')}</span>
                      </p>
                    ) : null}

                    <Select
                      prefix={isCompact ? <MapPin className='size-4' /> : null}
                      className='w-full border-none'
                      value={selection.field || ''}
                      options={fieldOptions}
                      placeholder={t('button_grid_field_placeholder')}
                      hasDeselectOption
                      onChange={(value) => handleFieldOrSupplierChange(i, 'field', value)}
                    />
                  </div>

                  <div>
                    {/* Material section */}
                    {!isCompact ? (
                      // Non-compact mode: Icon next to label
                      <p className='flex items-center gap-2 !m-0 !ml-0.5 !mb-1'>
                        <PieChart className='size-5' />
                        <span className='text-base'>{t('button_grid_material_label')}</span>
                      </p>
                    ) : null}

                    <Select
                      prefix={isCompact ? <PieChart className='size-4' /> : null}
                      className='w-full border-none'
                      value={selection.material || ''}
                      options={materialOptions}
                      placeholder={t('button_grid_material_placeholder')}
                      hasDeselectOption
                      loading={isLoadingCurrentButton && !dismissedNotifications[i]}
                      loadingText={t('button_grid_loading_suggestions')}
                      onChange={(value) => handleMaterialChange(i, value)}
                    />
                  </div>

                  {showSuggestionMessage ? (
                    <div className='absolute inset-x-4 -bottom-0.5 z-10 flex min-w-0 items-center gap-1'>
                      <EllipseTooltip title={suggestionMessage} className='min-w-0 flex-1 text-xs text-[#22C55E]'>
                        {suggestionMessage}
                      </EllipseTooltip>
                      <button
                        type='button'
                        className='shrink-0 text-[#90A1B9] hover:text-[#6B6B6B] dark:text-[#CAD5E2] dark:hover:text-[#FAFAFA]'
                        onClick={() => dismissNotification(i)}
                        title={t('button_grid_close_message')}
                      >
                        <X className='size-4' />
                      </button>
                    </div>
                  ) : null}
                </div>
              );
            } else {
              const isActive = isButtonActive(selection);
              const materialLabel = selection.material
                ? getLabelDisplayName(
                    defaultLabelsetGroups?.materials,
                    selection.material,
                    currentUser?.info?.languageCode,
                  ) || selection.material
                : null;
              const hasTimer = isActive && activeDocument?.data?.start_timestamp;

              return (
                <button
                  key={i}
                  className={cn(
                    'flex w-full min-h-0 min-w-0 flex-col rounded-2xl p-4 border shadow-[0_1px_3px_0_rgba(0,0,0,0.10),0_1px_2px_-1px_rgba(0,0,0,0.10)] dark:border-[#FFFFFF14] bg-[#E7EEF7] dark:bg-[#22293B] box-border align-center justify-between focus:outline-none focus-visible:outline-none',
                    (selection.supplier_id || selection.field) && 'cursor-pointer',
                    isActive && 'border-[#22C55E] bg-[#22C55E80] dark:border-[#22C55E] dark:bg-[#22C55E80]',
                  )}
                  onClick={() => toggleSelection(buttonSelection[i])}
                  disabled={!selection.supplier_id && !selection.field}
                >
                  <div className='flex w-full min-w-0 items-center gap-4 justify-between'>
                    {selection.material ? (
                      <div
                        className={cn(
                          'box-border text-sm font-medium px-2 py-1.5 border border-solid rounded-md self-start min-w-0 overflow-hidden',
                          hasTimer ? 'w-auto' : 'w-fit max-w-full',
                        )}
                        style={{
                          color: isActive ? '#6B6B6B' : getMaterialColor(selection.material),
                          backgroundColor: isActive ? '#F5F5F5' : `${getMaterialColor(selection.material)}14`,
                          borderColor: isActive ? '#B6B6B633' : `${getMaterialColor(selection.material)}33`,
                          boxShadow: `0 0 10px 0 ${getMaterialColor(selection.material)}1A`,
                        }}
                      >
                        <EllipseTooltip title={materialLabel} className='text-sm font-medium'>
                          {materialLabel}
                        </EllipseTooltip>
                      </div>
                    ) : (
                      <div
                        className={cn(
                          'box-border w-fit text-sm font-medium px-2 py-1.5 border border-solid rounded-md self-start',
                          isActive
                            ? 'bg-[#F5F5F5] text-[#6B6B6B] border-[#B6B6B633] shadow-[0_0_10px_0_#B6B6B61A]'
                            : 'bg-[#73758014] text-[#737580] border-[#73758033] dark:bg-[#cccccc14] dark:text-[#cccccc] dark:border-[#cccccc33]',
                        )}
                      >
                        <span>{t('button_grid_material_undefined')}</span>
                      </div>
                    )}
                    {hasTimer && (
                      <div className='flex shrink-0 items-center gap-1.5 px-2 py-1 rounded-2xl dark:bg-[#00000033] bg-[#0000004D]  font-semibold text-[#FAFAFA] dark:text-[#FAFAFA] tabular-nums whitespace-nowrap leading-[120%]'>
                        <Timer className='size-4 shrink-0' />
                        {nColumns <= 3
                          ? formatDuration(
                              activeDocument.data.start_timestamp?.toDate
                                ? activeDocument.data.start_timestamp.toDate()
                                : activeDocument.data.start_timestamp,
                            )
                          : formatDurationClock(
                              activeDocument.data.start_timestamp?.toDate
                                ? activeDocument.data.start_timestamp.toDate()
                                : activeDocument.data.start_timestamp,
                            )}
                      </div>
                    )}
                  </div>

                  {/* Rest of your button content remains unchanged */}
                  {!isRecipeControlEnabled || isIngredientInRecipe(selection.material, selection.field) ? (
                    <div className='flex flex-col gap-2 flex-1 items-center justify-center'>
                      <div className='text-[#16192C] dark:text-[#FAFAFA] text-xl font-semibold'>
                        {suppliers?.[selection.supplier_id]?.supplier_code ?? ''}{' '}
                        {suppliers?.[selection.supplier_id]?.display_name ?? selection.supplier_id}
                      </div>
                      <div className='field'>
                        {stockFields[selection.field]?.display_name || selection.field ? (
                          <span
                            className={cn(
                              'text-[#737580] dark:text-[#90A1B9] text-xl font-normal',
                              isActive && 'text-[#16192C] dark:text-[#FAFAFA]',
                            )}
                          >
                            {t('button_grid_field_prefix')}{' '}
                            {stockFields[selection.field]?.display_name || selection.field}
                          </span>
                        ) : (
                          <div
                            className={cn(
                              'text-[#737580] dark:text-[#90A1B9] text-xl',
                              isActive && 'text-[#16192C] dark:text-[#FAFAFA]',
                            )}
                          >
                            {t('button_grid_no_fields_defined')}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className='flex flex-col gap-2 flex-1 items-center justify-center'>
                      <div
                        className={cn(
                          ' text-[#737580] dark:text-[#90A1B9] font-semibold text-xl',
                          isActive && 'text-[#16192C] dark:text-[#FAFAFA]',
                        )}
                      >
                        {suppliers?.[selection.supplier_id]?.supplier_code ?? ''}{' '}
                        {suppliers?.[selection.supplier_id]?.display_name ?? selection.supplier_id}
                      </div>

                      <div className='field'>
                        {stockFields[selection.field]?.full_name || selection.field ? (
                          <div className='field'>
                            <span
                              className={cn(
                                ' text-[#737580] dark:text-[#90A1B9] text-xl font-normal focus:outline-none',
                                isActive && 'text-[#16192C] dark:text-[#FAFAFA] focus:outline-none',
                              )}
                            >
                              {t('button_grid_field_prefix')}{' '}
                              {stockFields[selection.field]?.full_name || selection.field}
                            </span>
                          </div>
                        ) : (
                          <div
                            className={cn(
                              'text-[#737580] dark:text-[#90A1B9] text-sm',
                              isActive && 'text-[#16192C] dark:text-[#FAFAFA]',
                            )}
                          >
                            {t('button_grid_no_fields_defined')}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </button>
              );
            }
          })}
        </div>
      </div>
    </div>
  );
};

export default ButtonGrid;
